import { Injectable } from '@angular/core';
import { LoadingService } from './loading.service';
import { MapDirectionsService } from '@angular/google-maps';

@Injectable({
  providedIn: 'root'
})
export class DistanceService {

  private currentPosition: GeolocationPosition = null;
  private lastKnownPosition: { lat: number; lng: number; ts: number } | null =
    null;
  private readonly lastKnownKey: string = 'LastKnownPosition';

  constructor(private mapDirectionsService: MapDirectionsService,
    private loading: LoadingService) {
    this.loadLastKnown();
  }

  private getCurrentPosition(options?: PositionOptions): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      console.log('[Distance] request current position', { ts: Date.now() });
      navigator.geolocation.getCurrentPosition(
        position => { console.log('[Distance] position success', { ts: Date.now(), coords: position?.coords }); resolve(position); },
        error => { console.warn('[Distance] position error', { ts: Date.now(), error }); reject(error); },
        options
      )
    });
  }

  private doCalcDistance(lng: number, lat: number): number {
    if (!this.currentPosition || lng === null || lat === null) {
      return 0;
    }
    return this.doCalcDistancePoints(lng, lat, this.currentPosition.coords.longitude, this.currentPosition.coords.latitude)
  }

  private doCalcDistancePoints(lng1: number, lat1: number, lng2: number, lat2: number): number {
    //    console.log('doCalcDistancePoints ' + lng1 + ' ' + lat1 + ' ' + lng2 + ' ' + lat2);
    var R = 6371 / 1.598; // Radius of the earth in km / 1598m/mi
    var dLat = this.deg2rad(lat2 - lat1);  // deg2rad below
    var dLon = this.deg2rad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
      ;
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c; // Distance in miles
    return +d.toFixed(1);
  }

  start(): Promise<void> {
    this.loading.inc('distance service');
    console.log('[Distance] start invoked', { ts: Date.now() });
    return new Promise((resolve, reject) => {
      this.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000,
      })
        .then((p) => {
          console.log('[Distance] start resolved with position', { ts: Date.now() });
          this.currentPosition = p;
          this.cachePosition(p);
          resolve();
        }, () => {
          console.warn('[Distance] start rejected (no position)', { ts: Date.now() });
          reject();
        })
        .finally(() => {
          this.loading.dec('distance service');
          console.log('[Distance] start finally', { ts: Date.now() });
        });
    });
  }

  distanceToGeo(geo: string): number {
    if (!geo) {
      return 0;
    }
    var pts = geo.split(',');
    if (pts.length == 2) {
      return this.distanceToPoint(parseFloat(pts[1]), parseFloat(pts[0]));
    }
    return 0;
  }

  distanceToGeoPoints(geo1: string, geo2: string): number {
    if (!geo1 || !geo2) {
      return 0;
    }
    var pts1 = geo1.split(',');
    var pts2 = geo2.split(',');
    if (pts1.length == 2 && pts2.length == 2) {
      return this.doCalcDistancePoints(parseFloat(pts1[0]), parseFloat(pts1[1]), parseFloat(pts2[0]), parseFloat(pts2[1]));
    }
    return 0;
  }

  distanceToPoint(lng: number, lat: number): number {
    return this.doCalcDistance(lng, lat);
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180)
  };

  myCurrentPosition(): google.maps.LatLngLiteral {
    if (this.currentPosition) {
      return { lng: this.currentPosition.coords.longitude, lat: this.currentPosition.coords.latitude };
    }
    if (this.lastKnownPosition) {
      return { lng: this.lastKnownPosition.lng, lat: this.lastKnownPosition.lat };
    }
    return null;
  }

  markerImage(url: string): HTMLElement {
    let imgTag = document.createElement('img');
    imgTag.height = 24;
    imgTag.width = 24;
    imgTag.src = url;

    return imgTag;
  }

  setImageCenter(x: IImageCenter, url: string): void {
    x.content = this.markerImage(url);

    x.currentDistance = this.distanceToPoint(x.longitude, x.latitude);
    if (x.latitude) {
      x.center = { lng: x.longitude, lat: x.latitude };
      x.hasCenter = true;
    } else {
      x.hasCenter = false;
    }
  }

  showDirections(appSelection: string, fullAddress: string): boolean {
    var url;
    switch (appSelection) {
      case 'google':
        url = 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(fullAddress);
        break;
      case 'apple':
        url = 'http://maps.apple.com/?daddr=' + encodeURIComponent(fullAddress);
        break;
      case 'waze':
        url = 'https://www.waze.com/ul?navigate=yes&q=' + encodeURIComponent(fullAddress);
        break;
    }
    if (url) {
      console.log(url);
      window.open(url);
      return true;
    }
    return false;
  }

  getAddressPart(e: google.maps.places.PlaceResult, p: string): string {
    var parts = e.address_components.filter((x) => x.types.indexOf(p) >= 0);
    if (parts.length > 0) {
      return parts[0].short_name;
    }
    return '';
  }

  getGoogleMapsRoute(allWaypoints: string[], optimize: boolean): Promise<google.maps.DirectionsResult> {
    return new Promise<google.maps.DirectionsResult>((resolve, reject) => {
      var waypoints = allWaypoints.slice(1, -1);

      const request: google.maps.DirectionsRequest = {
        origin: allWaypoints[0],
        destination: allWaypoints[allWaypoints.length - 1],
        waypoints: waypoints.map(x => { return { location: x }; }),
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: optimize
      };
      console.log(request);
      this.mapDirectionsService.route(request).subscribe((x) => {
        if (x) {
          console.log(x);
          resolve(x.result);
        }
      });
    });
  }

  private cachePosition(p: GeolocationPosition): void {
    if (!p?.coords) {
      return;
    }
    this.lastKnownPosition = {
      lat: p.coords.latitude,
      lng: p.coords.longitude,
      ts: Date.now(),
    };
    try {
      localStorage.setItem(this.lastKnownKey, JSON.stringify(this.lastKnownPosition));
    } catch { }
  }

  private loadLastKnown(): void {
    try {
      const raw = localStorage.getItem(this.lastKnownKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (
          typeof parsed?.lat === 'number' &&
          typeof parsed?.lng === 'number'
        ) {
          this.lastKnownPosition = {
            lat: parsed.lat,
            lng: parsed.lng,
            ts: parsed.ts || Date.now(),
          };
          this.currentPosition = {
            coords: {
              latitude: parsed.lat,
              longitude: parsed.lng,
              altitude: null,
              accuracy: null,
              altitudeAccuracy: null,
              heading: null,
              speed: null,
            },
            timestamp: parsed.ts || Date.now(),
          } as GeolocationPosition;
          console.log('[Distance] loaded cached position', {
            ts: Date.now(),
            cachedTs: this.lastKnownPosition.ts,
          });
        }
      }
    } catch { }
  }
}

export interface IImageCenter {
  longitude: number;
  latitude: number;
  currentDistance: number;
  center: google.maps.LatLngLiteral;
  hasCenter: boolean;
  content: HTMLElement;
}
